import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { ArticleDeliveryType } from 'graphql-ts-types';
import { color } from 'styled-system';

import type { ColorProps } from '../../theme/system-types';
import type { ArticleDeliveryDateAvailability } from '../../types/Article';
import { Flex } from '../Flex';
import { BodyRegularBR2 } from '../Typography';
import type { DeliveryDateRangeType } from './DeliveryDateRange';
import DeliveryDateRange from './DeliveryDateRange';

interface ArticleDeliveryFlags {
  fastDelivery?: boolean;
  deliveryToCurb: boolean;
}

interface DeliveryTimePeriodProps {
  type: ArticleDeliveryType;
  availability?: ArticleDeliveryDateAvailability;
  flags?: Nullable<ArticleDeliveryFlags>;
  overFreeShippingThreshold?: boolean;
  isProductCard?: boolean;
  className?: string;
  color?: ColorProps['color'];
  rangeType?: DeliveryDateRangeType;
}

const UnstyledContainer = styled(Flex)<any>`
  ${color};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer
    position="relative"
    pr="sp_32"
    justifyContent="space-between"
    alignItems={['flex-start', 'center']}
    flexDirection={['column', 'row']}
    {...props}
  />
);

const hasPossibleDeliveryDate = (
  availability: ArticleDeliveryDateAvailability
): availability is NonNullable<ArticleDeliveryDateAvailability> =>
  Boolean(availability?.possibleDeliveryDateMin && availability?.possibleDeliveryDateMax);

const DeliveryTimePeriod = ({
  type,
  availability,
  flags,
  overFreeShippingThreshold,
  isProductCard,
  className,
  color: textColor,
  rangeType,
  ...restProps
}: DeliveryTimePeriodProps) => {
  if (
    availability &&
    !hasPossibleDeliveryDate(availability) &&
    type !== ArticleDeliveryType.Virtual
  ) {
    return null;
  }

  const virtualDeliveryLabel = isProductCard
    ? 'h24_delivery_immediately_available'
    : 'h24_delivery_by_email';

  return (
    <Container className={className} {...restProps}>
      <BodyRegularBR2
        color={textColor}
        {...(flags?.fastDelivery ? { color: 'sys.success.text.default' } : {})}
        id="delivery-time-period"
      >
        <DeliveryDateRange
          {...availability}
          {...(overFreeShippingThreshold ? { overFreeShippingThreshold } : {})}
          type={rangeType}
          {...(type === ArticleDeliveryType.Virtual ? { virtualDeliveryLabel } : {})}
        />
      </BodyRegularBR2>
    </Container>
  );
};

export default DeliveryTimePeriod;
